/**
 * ExploreCamera — free 3rd-person camera for the home / explore screen.
 *
 * Orbits a ground `target` the user can PAN (drag), ROTATE (right-drag / two-finger),
 * and ZOOM (wheel / pinch). Starts pitched 45° down. Unlike the FPS camera it is not
 * driven by the player; unlike the old spectator camera it does not auto-orbit.
 *
 * The `target` doubles as the world's stream/shadow center while exploring, so panning
 * loads terrain where you look. State is module-level (one camera, one explore session).
 */

import * as THREE from 'three';

const target = new THREE.Vector3(0, 0, 0);
let yaw = 0;
let pitch = -Math.PI / 4;   // 45° down
let distance = 32;

// Look-down range: never flip past straight-down or up above the horizon.
const MIN_PITCH = -Math.PI / 2 + 0.05;
const MAX_PITCH = -0.12;
const MIN_DISTANCE = 6;
/** Zoom ceiling at base LOD 0 — extended by setExploreMaxDistance when far-LOD levels allow. */
const BASE_MAX_DISTANCE = 140;
let maxDistance = BASE_MAX_DISTANCE;

/**
 * Reference distance for the zoom→LOD mapping: base LOD stays 0 through the whole classic 6..140
 * range (promotion to LOD 1 happens at 70·2^1.25 ≈ 166 m with the ±0.25 hysteresis band).
 */
const ZOOM_REF_DISTANCE = 70;

const ROTATE_SPEED = 0.009;   // radians per pixel
const PAN_SPEED = 0.0016;     // world units per pixel, per unit distance
const KEY_MOVE_SPEED = 0.7;   // world units per second, per unit distance (WASD)
const ZOOM_SPEED = 0.0007;    // per wheel delta unit
const PINCH_ZOOM = 0.006;     // per pixel of pinch distance change

const _euler = new THREE.Euler(0, 0, 0, 'YXZ');
const _fwd = new THREE.Vector3();

// Recenter glide: eases `target` (ease-in-out) from where it was to a new spawn point, so
// tapping/dragging the spawn marker pans smoothly to re-center on it. Duration scales with the
// pan distance (a 400 m far-terrain tap takes ~1 s instead of a teleport-feel 350 ms snap).
const GLIDE_MIN_MS = 350;
const GLIDE_MAX_MS = 1200;
const GLIDE_METERS_PER_MS = 0.4;
const glideFrom = new THREE.Vector3();
const glideTo = new THREE.Vector3();
let glideDurationMs = GLIDE_MIN_MS;
let glideMs = 0;
let gliding = false;

// Zoom-in tween: eases `distance` from the current value to a target over a short, fixed
// duration with cosine ease-in-out. Triggered by the explore "zoom in" affordance so the
// camera doesn't snap from 200 m to ~22 m in one frame (that read as a teleport and made the
// marker feel like it flew off the screen). A user-initiated pan/rotate/zoom cancels the
// tween so manual input always wins.
//
// Easing choice: cosine ease-in-out is symmetric (velocity → 0 at both ends), but has a more
// perceptible deceleration phase than smoothstep at the end of the run — which matters here
// because the camera's visual delta shrinks as it approaches the target, so a soft tail is
// what reads as "settled" rather than "snapped". The 600 ms duration gives the deceleration
// phase enough frames to actually register.
const ZOOM_TWEEN_MS = 600;
let tweenFromDistance = 0;
let tweenToDistance = 0;
let tweenMs = 0;
let tweening = false;

// True while the user is grabbing/dragging the spawn marker; the center-follow that pins
// the spawn to screen center is suspended so the drag isn't immediately overridden.
let markerInteracting = false;

/** Seed the explore camera at a world center (e.g. saved player position). */
export function initExploreCamera(center: THREE.Vector3): void {
  target.copy(center);
  yaw = 0;
  pitch = -Math.PI / 4;
  distance = 32;
  gliding = false;
  tweening = false;
  markerInteracting = false;
}

/** Mark whether the user is actively manipulating the spawn marker (suspends center-follow). */
export function setExploreMarkerInteracting(v: boolean): void {
  markerInteracting = v;
}
export function isExploreMarkerInteracting(): boolean {
  return markerInteracting;
}

/** Start a smooth glide of the orbit target to `dest` (recenter on a moved spawn). */
export function beginExploreTargetGlide(dest: THREE.Vector3): void {
  glideFrom.copy(target);
  glideTo.copy(dest);
  const dist = glideFrom.distanceTo(glideTo);
  glideDurationMs = Math.max(GLIDE_MIN_MS, Math.min(GLIDE_MAX_MS, dist / GLIDE_METERS_PER_MS));
  glideMs = glideDurationMs;
  gliding = true;
}

/** True while a recenter glide is in progress (center-follow stays suspended meanwhile). */
export function isExploreGliding(): boolean {
  return gliding;
}

/** Where the glide is heading — the streaming centre goes there at once, the camera eases after. */
export function getExploreGlideDestination(): THREE.Vector3 {
  return glideTo;
}

/** Advance an in-progress recenter glide. Call each frame with the frame delta (ms). */
export function advanceExploreTargetGlide(deltaMs: number): void {
  if (!gliding) return;
  glideMs = Math.max(0, glideMs - deltaMs);
  const t = 1 - glideMs / glideDurationMs;
  const eased = t * t * (3 - 2 * t);   // smoothstep
  target.copy(glideFrom).lerp(glideTo, eased);
  if (glideMs === 0) gliding = false;
}

/** The current orbit target — also used as the world stream/shadow center. */
export function getExploreTarget(): THREE.Vector3 {
  return target;
}

/** Position + orient the camera from the current orbit state. Call each frame. */
export function updateExploreCamera(camera: THREE.PerspectiveCamera): void {
  _euler.set(pitch, yaw, 0, 'YXZ');
  _fwd.set(0, 0, -1).applyEuler(_euler);
  camera.position.copy(target).addScaledVector(_fwd, -distance);
  camera.rotation.order = 'YXZ';
  camera.rotation.set(pitch, yaw, 0);
}

/** Rotate the orbit (right-drag / two-finger). dx/dy in pixels. */
export function exploreCameraRotate(dx: number, dy: number): void {
  tweening = false; // manual input wins over an in-flight zoom-in tween
  yaw -= dx * ROTATE_SPEED;
  pitch -= dy * ROTATE_SPEED;
  if (pitch < MIN_PITCH) pitch = MIN_PITCH;
  if (pitch > MAX_PITCH) pitch = MAX_PITCH;
}

/** Pan the target across the ground plane (one-finger / left-drag). dx/dy in pixels. */
export function exploreCameraPan(dx: number, dy: number): void {
  tweening = false; // manual input wins over an in-flight zoom-in tween
  // Screen-right and screen-forward projected onto the horizontal plane (yaw only).
  const cy = Math.cos(yaw), sy = Math.sin(yaw);
  const rightX = cy, rightZ = -sy;   // (1,0,0) rotated by yaw about Y
  const fwdX = -sy, fwdZ = -cy;      // (0,0,-1) rotated by yaw about Y
  const s = distance * PAN_SPEED;
  // Drag right → world slides right (target moves left); drag down → target moves toward camera.
  target.x -= (rightX * dx - fwdX * dy) * s;
  target.z -= (rightZ * dx - fwdZ * dy) * s;
}

/**
 * Move the target across the ground plane from held keys (WASD/arrows). `moveX`/`moveZ` are the
 * local-space movement vector (+X right, -Z forward, length ≤ 1); speed scales with orbit distance
 * so travel feels the same at any zoom. Unlike pan this also cancels a recenter glide — the glide
 * rewrites the target every frame, and held keys are manual input, so it must yield.
 */
export function exploreCameraMove(moveX: number, moveZ: number, deltaMs: number): void {
  tweening = false;
  gliding = false;
  const cy = Math.cos(yaw), sy = Math.sin(yaw);
  const rightX = cy, rightZ = -sy;   // (1,0,0) rotated by yaw about Y
  const fwdX = -sy, fwdZ = -cy;      // (0,0,-1) rotated by yaw about Y
  const s = distance * KEY_MOVE_SPEED * (deltaMs / 1000);
  target.x += (rightX * moveX - fwdX * moveZ) * s;
  target.z += (rightZ * moveX - fwdZ * moveZ) * s;
}

/** Zoom the orbit distance. Positive `delta` zooms out (wheel down / pinch in). */
export function exploreCameraZoom(delta: number): void {
  tweening = false; // manual input wins over an in-flight zoom-in tween
  distance *= Math.exp(delta * ZOOM_SPEED);
  if (distance < MIN_DISTANCE) distance = MIN_DISTANCE;
  if (distance > maxDistance) distance = maxDistance;
}

/**
 * Snap the orbit distance to a target value (clamped to the active [MIN_DISTANCE, maxDistance]
 * range). Cancels any in-flight zoom-in tween — callers wanting the animated variant should use
 * `tweenExploreDistance` instead.
 */
export function setExploreDistance(target: number): void {
  tweening = false;
  distance = Math.max(MIN_DISTANCE, Math.min(maxDistance, target));
}

/**
 * Animate the orbit distance from its current value to `target` over ~ZOOM_TWEEN_MS with
 * smoothstep ease-in-out. Re-invoking restarts from wherever the tween (or the user) had
 * distance; a user-initiated pan/rotate/zoom cancels the tween (manual input wins).
 */
export function tweenExploreDistance(target: number): void {
  const clamped = Math.max(MIN_DISTANCE, Math.min(maxDistance, target));
  // No-op when already at the target — the click should still feel responsive, but a re-tween
  // with the same endpoints would do nothing visible and waste the frames.
  if (!tweening && Math.abs(distance - clamped) < 0.01) return;
  tweenFromDistance = distance;
  tweenToDistance = clamped;
  tweenMs = ZOOM_TWEEN_MS;
  tweening = true;
}

/**
 * Distance the UI should reason about: the tween's DESTINATION while one is in flight, else the
 * live value. The marker button reads this so tapping zoom-in reads as in-range on the next frame
 * instead of ~600 ms later — and, because a manual zoom cancels the tween, an abandoned zoom-in
 * snaps straight back to the real distance with nothing latched.
 */
export function getExploreEffectiveDistance(): number {
  return tweening ? tweenToDistance : distance;
}

/** Advance an in-flight zoom-in tween. Call each frame with the frame delta (ms). */
export function advanceExploreZoomTween(deltaMs: number): void {
  if (!tweening) return;
  tweenMs = Math.max(0, tweenMs - deltaMs);
  const t = 1 - tweenMs / ZOOM_TWEEN_MS;
  // Cosine ease-in-out: symmetric, with a clearly perceptible deceleration at both ends.
  const eased = (1 - Math.cos(Math.PI * t)) / 2;
  distance = tweenFromDistance + (tweenToDistance - tweenFromDistance) * eased;
  if (tweenMs === 0) tweening = false;
}

/**
 * Extend the zoom ceiling for the far-LOD system: maxBaseLod n allows distance up to
 * BASE_MAX·2^n (each base-LOD level doubles the sensible orbit range). 0 restores the classic cap.
 */
export function setExploreMaxBaseLod(maxBaseLod: number): void {
  maxDistance = BASE_MAX_DISTANCE * (1 << Math.max(0, maxBaseLod));
  if (distance > maxDistance) distance = maxDistance;
}

/** Current orbit distance (m). */
export function getExploreDistance(): number {
  return distance;
}

/** Fractional zoom level for the zoom→base-LOD mapping: 0 within the classic range, +1 per
 *  doubling of distance beyond the reference. */
export function getExploreZoomLevel(): number {
  return Math.max(0, Math.log2(distance / ZOOM_REF_DISTANCE));
}

/** Zoom from a pinch distance change (pixels); positive `deltaPixels` = fingers apart → zoom in. */
export function exploreCameraPinch(deltaPixels: number): void {
  exploreCameraZoom(-deltaPixels * PINCH_ZOOM / ZOOM_SPEED);
}

// ============== Lens shift (off-centre framing for the explore UI) ==============

/**
 * Framing offsets in CSS pixels: how far RIGHT and DOWN the orbit target should sit so it is
 * centred in the space the explore UI leaves free — the panel takes the left of the screen, and the
 * Play button + stem hang above the marker, so the world sits slightly low to centre that assembly.
 *
 * A LENS SHIFT (`setViewOffset`) rather than a lateral camera move: exact at any zoom, it does not
 * fight the user's pan, and the marker button — which projects through this same camera — follows
 * it for free.
 */
const VIEW_SHIFT_TAU_MS = 180;
let goalRightPx = 0;
let goalDownPx = 0;
let shiftRightPx = 0;
let shiftDownPx = 0;
let primed = false;
let appliedRight = NaN;
let appliedDown = NaN;
let appliedW = 0;
let appliedH = 0;
let appliedCamera: THREE.PerspectiveCamera | null = null;

/** How far right the world should sit (the explore panel's right edge ÷ 2). 0 = centred. */
export function setExploreViewShiftX(px: number): void {
  goalRightPx = px;
}

/** How far down the world should sit (half the Play button + stem assembly). 0 = centred. */
export function setExploreViewShiftY(px: number): void {
  goalDownPx = px;
}

/**
 * Ease the framing toward its goal and write it into the camera. Call EVERY frame in EVERY mode:
 * passing `active: false` (play, spectate) eases it back to centre over ~½ s, which is what keeps
 * the play transition smooth — applying the goal directly snapped the whole world sideways on the
 * transition frame, under a camera glide that takes 1.35 s.
 *
 * The FIRST framing of a session is the exception: it lands instantly. There is nothing to
 * transition from at boot, and easing in from centre read as the world drifting into place.
 */
export function advanceExploreViewShift(
  camera: THREE.PerspectiveCamera,
  deltaMs: number,
  active: boolean,
): void {
  const goalRight = active ? goalRightPx : 0;
  const goalDown = active ? goalDownPx : 0;

  if (!primed && active && (goalRight !== 0 || goalDown !== 0)) {
    primed = true;
    shiftRightPx = goalRight;
    shiftDownPx = goalDown;
  } else {
    const k = 1 - Math.exp(-deltaMs / VIEW_SHIFT_TAU_MS);
    shiftRightPx += (goalRight - shiftRightPx) * k;
    shiftDownPx += (goalDown - shiftDownPx) * k;
    // Land exactly, so an asymptotic tail doesn't rebuild the projection matrix forever.
    if (Math.abs(goalRight - shiftRightPx) < 0.25) shiftRightPx = goalRight;
    if (Math.abs(goalDown - shiftDownPx) < 0.25) shiftDownPx = goalDown;
  }

  const w = window.innerWidth;
  const h = window.innerHeight;
  if (
    shiftRightPx === appliedRight && shiftDownPx === appliedDown
    && w === appliedW && h === appliedH && camera === appliedCamera
  ) return;
  appliedRight = shiftRightPx;
  appliedDown = shiftDownPx;
  appliedW = w;
  appliedH = h;
  appliedCamera = camera;

  if (shiftRightPx === 0 && shiftDownPx === 0) {
    if (camera.view?.enabled) camera.clearViewOffset();
    return;
  }
  // fullWidth/Height === width/height, so this is a pure off-axis shift: same FOV, same aspect, no
  // distortion. The rendered window moves opposite the content, hence the negated offsets.
  camera.setViewOffset(w, h, -shiftRightPx, -shiftDownPx, w, h);
}
